// ATE-CR-117
import { useEffect, useState } from "react";

import { listCreditBalance, type CreditBalanceResource } from "@/api/credits.api";

interface Props {
  readonly tenantId: string;
}

// ATE-CR-117
export function BalanceTab({ tenantId }: Props): JSX.Element {
  const [resources, setResources] = useState<CreditBalanceResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    listCreditBalance(tenantId, ctrl.signal)
      .then((data) => {
        if (cancelled) return;
        setResources(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [tenantId, retryKey]);

  if (loading) {
    return <div role="status" className="credits-tab__loading">Loading balance…</div>;
  }

  if (error) {
    return (
      <div role="alert" className="credits-tab__error">
        Failed to load balance: {error.message}
        <button type="button" onClick={() => setRetryKey((k) => k + 1)}>Retry</button>
      </div>
    );
  }

  if (resources.length === 0) {
    return <p className="credits-tab__empty">No credit balance data found.</p>;
  }

  // ATE-CR-117 — task 11 FIX: render the real backend ResourceUsageEntry shape
  // (display_name + bonus_balance + total_remaining), no client-side `held`.
  return (
    <div className="balance-tab__cards">
      {resources.map((r) => (
        <div key={r.resource} className="balance-card">
          <h3 className="balance-card__resource">{r.display_name}</h3>
          <p className="balance-card__resource-key">{r.resource}</p>
          <dl className="balance-card__fields">
            <div className="balance-card__field">
              <dt>Daily Remaining</dt>
              <dd>
                {r.daily_remaining} / {r.daily_limit}
              </dd>
            </div>
            <div className="balance-card__field">
              <dt>Bonus</dt>
              <dd>{r.bonus_balance}</dd>
            </div>
            <div className="balance-card__field">
              <dt>Total Remaining</dt>
              <dd>{r.total_remaining}</dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  );
}
